import { isTestMode } from '@blueberry/framework/config'
import Tracker from '@openreplay/tracker'
import trackerAssist from '@openreplay/tracker-assist'
import * as Sentry from '@sentry/browser'
import { getLogger } from './logging'

export type OpenReplayConfig = {
  keys: {
    staging: string
    production: string
  }
}

const INGEST_POINTS = {
  dev: 'https://replay.dev.blueberrypediatrics.xyz/ingest',
  staging: 'https://replay.app.blueberrypediatrics.com/ingest',
  production: 'https://replay.app.blueberrypediatrics.com/ingest',
} as const

const logger = getLogger('open_replay')

const getEnvironment = (): 'dev' | 'staging' | 'production' => {
  if (typeof window == 'undefined') {
    return 'dev'
  }

  if (process.env.NODE_ENV === 'development') {
    return 'dev'
  }

  if (window.location.hostname.includes('staging')) {
    return 'staging'
  }

  return 'production'
}

export async function initializeReplayTracker(config: OpenReplayConfig) {
  if (typeof window == 'undefined') {
    return
  }

  if (isTestMode()) {
    return
  }

  if (getOpenReplayTracker()) {
    logger.info('Reusing existing replay tracker instance...')
    setMetadata()
    return
  }

  const environment = getEnvironment()
  if (environment === 'dev') {
    logger.info('OpenReplay is disabled in dev mode...')
    return
  }

  const projectKey = config.keys[environment]
  const ingestPoint = INGEST_POINTS[environment]

  logger.info('Initializing replay tracker...', {
    environment,
    projectKey,
    ingestPoint,
  })

  const tracker = new Tracker({
    projectKey,
    ingestPoint,
    capturePerformance: true,
    capturePageLoadTimings: true,
    captureResourceTimings: true,
    capturePageRenderTimings: true,
    respectDoNotTrack: false,
    obscureTextEmails: false,
    defaultInputMode: 0, // Prevents obscuring text input fields
  })

  // https://docs.openreplay.com/en/tutorials/assist/
  tracker.use(trackerAssist())

  tracker.event('mounted', { sessionId: tracker.sessionID() })

  if (!tracker.isActive()) {
    logger.info('starting replay tracker...')

    tracker
      .start({
        forceNew: true,
      })
      .then((session) => {
        logger.info(
          'Created new session: ',
          session.success,
          getOpenReplayTracker()?.sessionID(),
        )
        attachOpenReplayHeadersToFrameRequests()
        setMetadata()
      })
  } else {
    logger.info('tracker already active...')
  }

  ;(window as any).__OpenReplay = tracker
}

export const getOpenReplayTracker = (): Tracker | null => {
  if (typeof window === 'undefined') {
    return null
  }

  return (window as any).__OpenReplay
}

const setMetadata = () => {
  const tracker = getOpenReplayTracker()
  if (!tracker) {
    logger.error('[setMetadata] tracker not found...')
    return
  }

  Sentry.setExtra('openReplaySessionID', tracker.getSessionID())
  Sentry.setExtra('openReplaySessionURL', tracker.getSessionURL())
  const email = getUserEmail()
  if (email) {
    logger.info('[setMetadata] replay tracker user ID...', email)
    tracker.setUserID(email)
  }
}

const getUserEmail = (): string | null => {
  return (
    document
      .querySelector('meta[name="user_email"]')
      ?.getAttribute('content') ?? null
  )
}

const attachOpenReplayHeadersToFrameRequests = () => {
  window.addEventListener('turbo:before-fetch-request', (event) => {
    try {
      const tracker = getOpenReplayTracker()
      if (!tracker) {
        return
      }

      const request = (event as any)?.detail?.fetchOptions
      if (!request) {
        return
      }

      if (!request.headers) {
        request.headers = new Headers()
      }

      request.headers['X-OpenReplay-SessionId'] = tracker.getSessionID()
      request.headers['X-OpenReplay-SessionUrl'] = tracker.getSessionURL()

      if (tracker.getSessionToken()) {
        request.headers['X-OpenReplay-SessionToken'] = tracker.getSessionToken()
      }
    } catch (e) {
      logger.error('Failed to attach headers to turbo request', e)
    }
  })
}
